// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	performGetRequest,
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	AdminSettingsRequest,
	AdminSettingsResponse,
	AdminSettingsMap,
	BannerSettings,
	UserSetting,
	UserSettingsRequest,
	AssetsLogoResponse,
	AssetsFaviconResponse,
} from './BannerSettings.types.tsx';
import {
	getAdminSettingsUrl,
	getBannerSettingsUrl,
	getUserSettingsUrl,
	getLogoUrl,
	getFaviconUrl,
	transformAdminSettingsResponse,
	transformBannerSettings,
	mergeOptionsWithDefaults,
	getAssetsTenantContextMetadata,
	getAssetsAdminHeaders,
} from './extractResourceStatePromise.tsx';

const packageName = 'jiraRouterResourcesAssetsAdminSettings';
const teamName = 'astral';

/**
 * Fetches admin settings by keys for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/settings
 */
export const getAdminSettingsByKeys = async (
	workspaceId: string,
	settingKeys: AdminSettingsRequest[],
): Promise<AdminSettingsMap> => {
	const url = getAdminSettingsUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		body: JSON.stringify(settingKeys),
		headers: getAssetsAdminHeaders(workspaceId),
	});

	try {
		const response = await performPostRequest<AdminSettingsResponse>(url, mergedOptions);

		return transformAdminSettingsResponse(response);
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const tenantContextMetadata = getAssetsTenantContextMetadata();

		logger.safeErrorWithoutCustomerData(`${packageName}.getAdminSettingsByKeys`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
			tenantContextMetadata: JSON.stringify(tenantContextMetadata || {}),
		});

		fireErrorAnalytics({
			meta: {
				id: 'getAdminSettingsByKeys',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Fetches banner settings for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings
 */
export const getBannerSettings = async (workspaceId: string): Promise<BannerSettings> => {
	const url = getBannerSettingsUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: getAssetsAdminHeaders(workspaceId),
	});

	try {
		const response = await performGetRequest<BannerSettings>(url, mergedOptions);

		return transformBannerSettings(response);
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const tenantContextMetadata = getAssetsTenantContextMetadata();

		logger.safeErrorWithoutCustomerData(`${packageName}.getBannerSettings`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
			tenantContextMetadata: JSON.stringify(tenantContextMetadata || {}),
		});

		fireErrorAnalytics({
			meta: {
				id: 'getBannerSettings',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates banner settings for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings/<banner-id>
 */
export const updateBannerSettings = async (
	workspaceId: string,
	bannerId: string,
	bannerUpdates: BannerSettings,
): Promise<BannerSettings> => {
	const url = `${getBannerSettingsUrl(workspaceId)}/${bannerId}`;

	try {
		const response = await performPutRequest<BannerSettings>(
			url,
			mergeOptionsWithDefaults(url, {
				body: JSON.stringify(transformBannerSettings(bannerUpdates)),
			}),
		);
		return transformBannerSettings(response);
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData(`${packageName}.updateBannerSettings`, error.message, {
			workspaceId,
			bannerId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
		});

		fireErrorAnalytics({
			meta: {
				id: 'updateBannerSettings',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Fetches user settings for the current user
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/user/settings?key=<key>
 */
export const getUserSettings = async (workspaceId: string, key: string): Promise<UserSetting> => {
	const url = `${getUserSettingsUrl(workspaceId)}?key=${key}`;

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: getAssetsAdminHeaders(workspaceId),
	});

	try {
		const response = await performGetRequest<UserSetting>(url, mergedOptions);
		return response;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const tenantContextMetadata = getAssetsTenantContextMetadata();

		logger.safeErrorWithoutCustomerData(`${packageName}.getUserSettings`, error.message, {
			workspaceId,
			key,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
			tenantContextMetadata: JSON.stringify(tenantContextMetadata || {}),
		});

		fireErrorAnalytics({
			meta: {
				id: 'getUserSettings',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates user settings for the current user
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/user/settings
 */
export const updateUserSettings = async (
	workspaceId: string,
	settings: UserSettingsRequest[],
): Promise<void> => {
	const url = getUserSettingsUrl(workspaceId);

	try {
		await performPutRequest(url, { body: JSON.stringify(settings) });
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData(`${packageName}.updateUserSettings`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
		});

		fireErrorAnalytics({
			meta: {
				id: 'updateUserSettings',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates navigation settings for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/settings
 */
export const updateNavigationSettings = async (
	workspaceId: string,
	navigationUpdates: AdminSettingsResponse,
): Promise<AdminSettingsMap> => {
	const url = getAdminSettingsUrl(workspaceId);

	try {
		return await performPutRequest<AdminSettingsMap>(url, {
			body: JSON.stringify(navigationUpdates),
		});
	} catch (error: unknown) {
		const err = error instanceof Error ? error : new Error(String(error));

		logger.safeErrorWithoutCustomerData(`${packageName}.updateNavigationSettings`, err.message, {
			workspaceId,
			// @ts-expect-error - statusCode may not exist on Error
			statusCode: err?.statusCode,
			errorName: err?.name,
			// @ts-expect-error - originalResponse may not exist on Error
			description: err?.originalResponse?.message,
		});

		fireErrorAnalytics({
			meta: {
				id: 'updateNavigationSettings',
				packageName,
				teamName,
			},
			error: err,
		});
		throw err;
	}
};

/**
 * Fetches logo for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/assets-logo
 */
export const getLogo = async (workspaceId: string): Promise<AssetsLogoResponse> => {
	const url = getLogoUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: getAssetsAdminHeaders(workspaceId),
	});

	try {
		const response = await performGetRequest<AssetsLogoResponse>(url, mergedOptions);
		return response;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const tenantContextMetadata = getAssetsTenantContextMetadata();

		logger.safeErrorWithoutCustomerData(`${packageName}.getLogo`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
			tenantContextMetadata: JSON.stringify(tenantContextMetadata || {}),
		});

		fireErrorAnalytics({
			meta: {
				id: 'getLogo',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Fetches favicon for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/favicon
 */
export const getFavicon = async (workspaceId: string): Promise<AssetsFaviconResponse> => {
	const url = getFaviconUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: getAssetsAdminHeaders(workspaceId),
	});

	try {
		const response = await performGetRequest<AssetsFaviconResponse>(url, mergedOptions);
		return response;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const tenantContextMetadata = getAssetsTenantContextMetadata();

		logger.safeErrorWithoutCustomerData(`${packageName}.getFavicon`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
			tenantContextMetadata: JSON.stringify(tenantContextMetadata || {}),
		});

		fireErrorAnalytics({
			meta: {
				id: 'getFavicon',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Uploads logo for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/assets-logo/upload
 */
export const uploadLogo = async (
	workspaceId: string,
	payload: { file?: File; fileName?: string; imageUrl?: string },
): Promise<AssetsLogoResponse> => {
	try {
		const url = `${getLogoUrl(workspaceId)}/upload`;
		const formData = new FormData();
		if (payload.file && payload.fileName) {
			formData.append('assets-logo-file', payload.file);
			formData.append('fileName', payload.fileName);
		} else if (payload.imageUrl) {
			formData.append('imageUrl', payload.imageUrl);
		}

		const response = await performPostRequest<AssetsLogoResponse>(url, {
			body: formData,
			headers: {},
		});
		return response;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData(`${packageName}.uploadLogo`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
		});

		fireErrorAnalytics({
			meta: {
				id: 'uploadLogo',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Uploads favicon for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/favicon/upload
 */
export const uploadFavicon = async (
	workspaceId: string,
	payload: { file?: File; fileName?: string; imageUrl?: string },
): Promise<AssetsFaviconResponse> => {
	try {
		const url = `${getFaviconUrl(workspaceId)}/upload`;
		const formData = new FormData();
		if (payload.file && payload.fileName) {
			formData.append('assets-favicon-file', payload.file);
			formData.append('fileName', payload.fileName);
		} else if (payload.imageUrl) {
			formData.append('imageUrl', payload.imageUrl);
		}

		const response = await performPostRequest<AssetsFaviconResponse>(url, {
			body: formData,
			headers: {},
		});
		return response;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData(`${packageName}.uploadFavicon`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
		});

		fireErrorAnalytics({
			meta: {
				id: 'uploadFavicon',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Resets logo to default for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/assets-logo/reset-to-default
 */
export const resetLogoToDefault = async (workspaceId: string): Promise<AssetsLogoResponse> => {
	try {
		const url = `${getLogoUrl(workspaceId)}/reset-to-default`;
		const response = await performPutRequest<AssetsLogoResponse>(url);
		return response;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData(`${packageName}.resetLogoToDefault`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
		});

		fireErrorAnalytics({
			meta: {
				id: 'resetLogoToDefault',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Resets favicon to default for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/favicon/reset-to-default
 */
export const resetFaviconToDefault = async (
	workspaceId: string,
): Promise<AssetsFaviconResponse> => {
	try {
		const url = `${getFaviconUrl(workspaceId)}/reset-to-default`;
		const response = await performPutRequest<AssetsFaviconResponse>(url);
		return response;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData(`${packageName}.resetFaviconToDefault`, error.message, {
			workspaceId,
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
		});

		fireErrorAnalytics({
			meta: {
				id: 'resetFaviconToDefault',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};
